import Flex from '@eversports/react-components/primitives/Flex'
import MonetaryValue from '@eversports/react-components/primitives/MonetaryValue'
import Text from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { Localized } from '../../localization/react'
import { getCurrentTicketPrice, PurchaseOption, TicketPriceType } from '../../typings/Product'

interface Props {
  product: PurchaseOption
}

const useStyles = makeStyles({
  strikethrough: {
    textDecoration: 'line-through',
  },
})

const ProductPrice: React.FC<React.PropsWithChildren<Props>> = ({ product }) => {
  const classes = useStyles()

  switch (product.__typename) {
    case 'Membership':
      const price = product.paymentPlan.initial ? product.paymentPlan.initial : product.paymentPlan.recurring
      const promotionalPrice = product.paymentPlan.promotion?.price

      return (
        <Flex flexDirection="column" justifyContent="center">
          {product.paymentPlan.promotion && (
            <Text className={classes.strikethrough} color="subtleText">
              <MonetaryValue
                value={product.paymentPlan.recurring.value}
                currencyCode={product.paymentPlan.recurring.currency}
              />
            </Text>
          )}
          <Text fontWeight="bold">
            <MonetaryValue
              value={promotionalPrice?.value || price.value}
              currencyCode={promotionalPrice?.currency || price.currency}
            />
          </Text>
          <Text size="x-small" color="subtleText" fontWeight="bold">
            <Localized
              id="membership-interval"
              params={{
                interval: (
                  <Localized
                    id="interval"
                    params={{
                      intervalValue: product.paymentPlan.interval.value,
                      intervalType: product.paymentPlan.interval.type as any,
                    }}
                  />
                ),
              }}
            />
          </Text>
        </Flex>
      )
    case 'Ticket':
      if (
        product.currentPriceType === TicketPriceType.TICKET_PRICE_TYPE_STANDARD ||
        !product.productPurchaseInformation.purchasable
      ) {
        return (
          <Text fontWeight="bold">
            <MonetaryValue value={product.standardPrice.value} currencyCode={product.standardPrice.currency} />
          </Text>
        )
      }

      const discountedPrice = getCurrentTicketPrice(product)

      return (
        <Flex flexDirection="column" justifyContent="center">
          <Text fontWeight="bold">
            <MonetaryValue value={discountedPrice.value} currencyCode={discountedPrice.currency} />
          </Text>
          <Text className={classes.strikethrough}>
            <MonetaryValue value={product.standardPrice.value} currencyCode={product.standardPrice.currency} />
          </Text>
        </Flex>
      )
    case 'Card':
      return (
        <Flex flexDirection="column" justifyContent="center">
          <Text fontWeight="bold">
            <MonetaryValue value={product.price.value} currencyCode={product.price.currency} />
          </Text>
          {product.isCorporatePrice && product.originalPrice && (
            <Text className={classes.strikethrough}>
              <MonetaryValue value={product.originalPrice.value} currencyCode={product.originalPrice.currency} />
            </Text>
          )}
        </Flex>
      )
    case 'SinglePurchase':
      return (
        <Text fontWeight="bold">
          <MonetaryValue value={product.price.value} currencyCode={product.price.currency} />
        </Text>
      )
    default:
      return assertNever(product)
  }
}

export default ProductPrice
